import type { EngageMailbox } from '@/store/features/engage.feature';
import type { SendMailboxEmailConfig } from '@/types/custom-playbooks';

/**
 * The step "Send an email": one email a person from a member's own Gmail or
 * Outlook mailbox. Mirrors cleanse-api `utils/play_mailbox_email.py`; the
 * server's validator, save check (only the mailbox's owner may pick it) and
 * send checks are the authority.
 */

export type MailboxProvider = SendMailboxEmailConfig['provider'];

export const MAILBOX_PROVIDERS: readonly MailboxProvider[] = [
  'google_workspace',
  'microsoft_365',
];

export const MAILBOX_PROVIDER_NAMES: Record<MailboxProvider, string> = {
  google_workspace: 'Gmail',
  microsoft_365: 'Outlook',
};

export const SUBJECT_TEMPLATE_LIMIT = 300;
export const BODY_TEMPLATE_LIMIT = 20_000;

/** The code a person the step didn't email carries, with the reason. */
export const MAILBOX_EMAIL_SKIP_CODE = 'mailbox_email_skipped';

export function isMailboxProvider(value: unknown): value is MailboxProvider {
  return MAILBOX_PROVIDERS.includes(value as MailboxProvider);
}

/**
 * The mailboxes the member may pick: their own, connected and turned on, of
 * the step's provider (either email provider while none is set). Never a
 * teammate's, shared or not: the server refuses any other.
 */
export function ownMailboxes(
  mailboxes: readonly EngageMailbox[],
  userId: string | null | undefined,
  provider: MailboxProvider | undefined
): EngageMailbox[] {
  if (!userId) return [];
  return mailboxes.filter(
    (mailbox) =>
      mailbox.owner_user_id === userId &&
      mailbox.connected &&
      mailbox.active &&
      (provider
        ? mailbox.provider === provider
        : isMailboxProvider(mailbox.provider))
  );
}

/** The state beside a saved mailbox the member can't pick, or null when
 *  it is one of theirs and ready. */
export function savedMailboxState(
  mailbox: EngageMailbox | undefined,
  userId: string | null | undefined
): string | null {
  if (!mailbox) return 'Not found';
  if (mailbox.owner_user_id !== userId) return 'Teammate’s mailbox';
  if (!mailbox.connected) return 'Disconnected';
  if (!mailbox.active) return 'Off';
  return null;
}

/** Why a Send an email step can't run yet, or null. */
export function sendMailboxEmailIssue(
  config: Partial<SendMailboxEmailConfig> | undefined
): string | null {
  const subject = config?.subject ?? '';
  const body = config?.body ?? '';
  if (!config?.mailboxId) return 'Pick the mailbox that sends';
  if (!isMailboxProvider(config.provider)) return 'Pick Gmail or Outlook';
  if (!subject.trim()) return 'Write the subject';
  if (subject.length > SUBJECT_TEMPLATE_LIMIT)
    return `A subject is at most ${SUBJECT_TEMPLATE_LIMIT} characters`;
  if (!body.trim()) return 'Write the email';
  if (body.length > BODY_TEMPLATE_LIMIT)
    return 'An email is at most 20,000 characters';
  return null;
}

/** "From rep@acme.com: Subject", as the API's step_summary. */
export function sendMailboxEmailSummary(
  config: Partial<SendMailboxEmailConfig> | undefined
): string {
  const from = config?.mailboxId ? config.mailboxEmail?.trim() : '';
  if (!from) return 'Pick the mailbox that sends';
  const where = config?.mode === 'draft' ? `Draft in ${from}` : `From ${from}`;
  const subject = config?.subject?.trim();
  if (!subject) return where;
  const shown = subject.length <= 60 ? subject : `${subject.slice(0, 59)}…`;
  return `${where}: ${shown}`;
}

/** What follows the tool on the card's headline: the address once a
 *  mailbox is picked, else what the step does (the API's headline_words). */
export function sendMailboxEmailHeadlineWords(
  config: Partial<SendMailboxEmailConfig> | undefined,
  action: string
): string {
  return config?.mailboxEmail?.trim() || action;
}
